import MaterialTable from '@material-table/core';
import { EventRepeatOutlined, ScheduleOutlined } from '@mui/icons-material';
import {
	Box, Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	LinearProgress,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography
} from '@mui/material';
import { generateClient, post } from 'aws-amplify/api';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { US } from 'country-region-data';
import { Formik } from 'formik';
import * as _ from 'lodash';
import phone from 'phone';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import {
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { array, lazy, mixed, object, reach, string } from 'yup';
import { BulkScheduleDialog } from '../../components/contacts/bulkScheduleDialog';
import { SegmentPicker } from '../../components/contacts/segmentPicker';
import { ConfirmDialog } from '../../components/confirmDialog/confirmDialog';
import { PageAppBar } from '../../components/pageAppBar';
import QueryBuilder from '../../components/queryBuilder';
import { contactValidation } from '../../components/yupValidations';
import { UserContext } from '../../contexts/UserContext';
import { expandedContactSearch, } from '../../graphql/queries';
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle, destructiveButtonStyle } from '../../theme';
import { deriveCustomFieldColumns, parseQbQuery, toBuilderQuery } from '../../tools/segmentQuery';



const types = {
	String: 0,
	ISODateTime: 1,
	Boolean: 2,
	Timezone: 3,
	State: 4,
	Epoch: 5,
	DateTime: 5, // The custom field DateTime is an epoch.
	Number: 6,
	Phone: 7
};

const modes = {
	Delete: 0,
	Update: 1
};

/**
 * Rejection reasons from `parseQbQuery` that mean the segment never had a query
 * to load, as opposed to having one that cannot be read. The two groups get
 * different snackbar text because they need different follow-up: nothing was
 * ever saved (rebuild and save the segment) versus something was saved in a
 * shape this page cannot use (a data problem worth reporting).
 */
const NO_SAVED_QUERY_REASONS = ['absent', 'empty'];

/**
 * Snackbar text for a segment whose stored query could not be turned into a
 * query builder value. One generic message for every reason left an operator
 * with nothing to tell us; these two at least separate "there is no query" from
 * "the query is unreadable".
 *
 * @param {string} reason A rejection reason from `parseQbQuery`.
 * @returns {string} The message to show.
 */
function segmentLoadErrorMessage(reason) {
	// Unknown reasons fall to the unreadable message rather than throwing: a new
	// reason added to parseQbQuery must not break the page.
	return NO_SAVED_QUERY_REASONS.includes(reason)
		? 'That segment has no saved query to load'
		: `That segment's stored query could not be read`;
}

export function BulkEdit() {
	const navigate = useNavigate();
	const usStates = US[2];
	const client = generateClient();
	const userContext = useContext(UserContext);
	const [total, setTotal] = useState(0);
	const [retrievedCount, setRetrievedCount] = useState(0);
	const newSearchFilter = useRef({});
	const [contacts, setContacts] = useState([]);
	// Preview-only progress. Set where the query changes rather than where the
	// request fires, because `queryPreviewThrottler` holds the call for up to a
	// second and that dead second is what reads as nothing happening.
	const [previewLoading, setPreviewLoading] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [queryFields, setQueryFields] = useState([]);
	const [mode, setMode] = useState('Update');
	const [columns, setColumns] = useState([
		{ title: 'First Name', field: 'firstName' },
		{ title: 'Last Name', field: 'lastName' },
		{ title: 'Source', field: 'source' },
		{ title: 'Phone', field: 'phone' },
		{ title: 'Cell', field: 'cell' },
		{ title: 'Email', field: 'email' },
		{ title: 'State', field: 'state' },

	]);

	const progressCountRef = useRef(0);
	const [progressCount, setProgressCount] = useState(0);
	const errorCountRef = useRef(0);
	const [errorCount, setErrorCount] = useState(0);
	const [progressDialogOpen, setProgressDialogOpen] = useState(false);
	const [processDialogTitle, setProcessDialogTitle] = useState('');
	const queryPreviewThrottler = useRef(_.throttle((query) => logQuery(query), 1000, { 'leading': false, 'trailing': true }));
	const [customFieldColumns, setCustomFieldColumns] = useState([]);
	const [scheduleDeleteOpen, setScheduleDeleteOpen] = useState(false);
	const [deleteTime, setDeleteTime] = useState(moment().add(1, 'day').format('YYYY-MM-DDTHH:mm'));
	const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
	const [recurringAction, setRecurringAction] = useState('Update');

	// Segment load state. `selectedSegment` drives the (controlled) picker and is
	// only advanced once a load is actually applied, so cancelling the
	// replacement confirmation leaves the input on its prior selection.
	const [selectedSegment, setSelectedSegment] = useState(null);
	// { id, name } of the segment the current query was loaded from. Reference
	// only: the page never writes back to the Segment record.
	const [sourceSegment, setSourceSegment] = useState(null);
	// { segment, query } awaiting confirmation of a destructive replacement.
	const [pendingSegment, setPendingSegment] = useState(null);
	const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);

	const csvChunks = useRef([]);

	const valueInputTypes = useMemo(() => ({
		firstName: types.String,
		lastName: types.String,
		phone: types.Phone,
		source: types.String,
		cell: types.Phone,
		email: types.String,
		address1: types.String,
		address2: types.String,
		city: types.String,
		state: types.State,
		zip: types.String,
		outboundCallerId: types.String,
		assignedAgent: types.String,
		ttl: types.Epoch,
		...userContext.customFields?.reduce((acc, cur) => Object.assign(acc, { [`custom:${cur.name}`]: types[cur.type] }), {})
	}), [userContext.customFields]);

	// Rebuilds the preview columns on every change to the derived custom field
	// list, including a change back to an empty list — guarding on
	// `customFieldColumns.length > 0` would leave the columns of a previous
	// query on screen after its custom rules were removed.
	useEffect(() => {
		const columns = [
			{ title: 'First Name', field: 'firstName' },
			{ title: 'Last Name', field: 'lastName' },
			{ title: 'Source', field: 'source' },
			{ title: 'Phone', field: 'phone' },
			{ title: 'Cell', field: 'cell' },
			{ title: 'Email', field: 'email' },
			{ title: 'State', field: 'state' },
		];
		for (const customField of customFieldColumns) {
			// A loaded query can reference a custom field that has since been
			// deleted from the tenant, so a missing definition is skipped rather
			// than dereferenced.
			const definition = userContext.customFields?.find(field => field.name === customField);
			if (!definition) {
				continue;
			}
			columns.push({ title: definition.displayName, field: `customFields.${customField}` });
		}
		setColumns(columns);
	}, [customFieldColumns, userContext.customFields]);


	useEffect(() => {
		if (userContext.queryFields.list) {
			setQueryFields([...userContext.queryFields.list])
		}
	}, [userContext.queryFields])

	/**
	 * The single point where a query builder value change fans out to the rest
	 * of the page. `QueryBuilder` re-syncs its internal state from `props.query`
	 * without notifying, so a value pushed in from outside the builder fires
	 * none of these side effects on its own and has to come through here.
	 *
	 * @param {{rules: any[], combinator: string}} query The new query value.
	 * @param {object} formikProps The render-prop bag from Formik.
	 * @returns {void}
	 */
	function applyQueryChange(query, formikProps) {
		formikProps.setFieldValue('query', query);
		// Feedback starts here, at the operator's edit, not in `previewContacts`:
		// the throttler's trailing window means the request itself is up to a
		// second away. An empty query is never searched (`logQuery` only resets
		// `total`), so there is nothing to wait for in that case.
		setPreviewLoading((query?.rules?.length ?? 0) > 0);
		queryPreviewThrottler.current(query);
		setCustomFieldColumns(deriveCustomFieldColumns(query));
	}

	/**
	 * Handles a selection (or a clear) coming out of the segment picker.
	 *
	 * Clearing drops the source attribution and deliberately leaves the query
	 * builder value alone — the operator keeps the rules they are working with.
	 * A segment whose stored query cannot be parsed, or that parses to a query
	 * with no rules, is reported and ignored, and the picker keeps its prior
	 * value because `selectedSegment` only advances in `applySegment`. With rules
	 * already in the builder the replacement is confirmed first, since it
	 * discards them.
	 *
	 * Every exit from here is either an applied load or a notification: a
	 * selection that changes nothing on screen and says nothing is
	 * indistinguishable from a broken picker.
	 *
	 * @param {object|null} segment The selected Segment, or null when cleared.
	 * @param {object} formikProps The render-prop bag from Formik.
	 * @returns {void}
	 */
	function handleSegmentSelect(segment, formikProps) {
		if (!segment) {
			setSelectedSegment(null);
			setSourceSegment(null);
			return;
		}

		const parsed = parseQbQuery(segment.query?.qbQuery);

		if (!parsed.ok) {
			console.error('Unable to load segment query', segment.id, parsed.reason);
			enqueueSnackbar(segmentLoadErrorMessage(parsed.reason), { variant: 'error' });
			return;
		}

		// Narrowed here, at the one place the parsed query enters the page, so both
		// the immediate-apply path below and the `pendingSegment` confirmation path
		// hold the normalized value. A stored query saved from the Segments page
		// carries a `sort` key, and letting that reach `values.query.sort` makes
		// QueryBuilder's `[values.query.sort]` effect re-emit its pre-load root and
		// wipe this load — see `toBuilderQuery`.
		const builderQuery = toBuilderQuery(parsed.query);

		// Parsed, but there are no rules to load. Checked BEFORE the replacement
		// confirmation: an operator should never be asked to discard their rules
		// in exchange for nothing.
		//
		// The query field is deliberately left untouched. Applying an empty query
		// would either wipe rules the operator is working with — a destructive
		// result from a load that had nothing to give — or, on an already empty
		// builder, contribute no more than the stored combinator. A no-op plus a
		// notification is the better trade in both cases, so the load is refused
		// and `selectedSegment` / `sourceSegment` stay where they were rather than
		// attributing the builder to a segment nothing came from.
		if (builderQuery.rules.length === 0) {
			console.warn('Segment query has no rules', segment.id);
			enqueueSnackbar(`That segment's saved query is empty, so there was nothing to load`, { variant: 'warning' });
			return;
		}

		if ((formikProps.values.query?.rules ?? []).length === 0) {
			applySegment(segment, builderQuery, formikProps);
			return;
		}

		setPendingSegment({ segment, query: builderQuery });
		setReplaceConfirmOpen(true);
	}

	/**
	 * Applies a parsed segment query to the page: a full replacement of the
	 * query builder value, never a merge with the existing rules, fanned out
	 * through the same path a manual edit takes.
	 *
	 * @param {object} segment The Segment the query came from.
	 * @param {{rules: any[], combinator: string}} query The parsed query value.
	 * @param {object} formikProps The render-prop bag from Formik.
	 * @returns {void}
	 */
	function applySegment(segment, query, formikProps) {
		applyQueryChange(query, formikProps);
		setSelectedSegment(segment);
		setSourceSegment({ id: segment.id, name: segment.name });
	}

	/**
	 * Confirms the pending replacement.
	 *
	 * @param {object} formikProps The render-prop bag from Formik.
	 * @returns {void}
	 */
	function handleConfirmReplace(formikProps) {
		if (pendingSegment) {
			applySegment(pendingSegment.segment, pendingSegment.query, formikProps);
		}

		setPendingSegment(null);
		setReplaceConfirmOpen(false);
	}

	/**
	 * Cancels the pending replacement. A no-op beyond dropping the pending
	 * selection: the query, the source attribution, and the picker's displayed
	 * selection are all left as they were.
	 *
	 * @returns {void}
	 */
	function handleCancelReplace() {
		setPendingSegment(null);
		setReplaceConfirmOpen(false);
	}

	function standardizeValue(values) {
		try {
			let value
			switch (valueInputTypes[values.fieldName]) {
				case types.String:
					return values.fieldValue;
				case types.Phone:
					return phone(values.fieldValue, { validateMobilePrefix: false }).phoneNumber || values.fieldValue;
				case types.ISODateTime:
					return new Date(values.fieldValue).toISOString();
				case types.Epoch:
					return new Date(values.fieldValue).getTime() / 1000;
				case types.Number:
					return Number(values.fieldValue)
				case types.Boolean:
					return !!values.fieldValue;
				case types.State:
				case types.Timezone:
					return values.fieldValue;
				default:
					return null;
			}

		} catch (err) {
			console.error('Unable to get value', err)
		}

	}

	const logQuery = (query) => {
		if (query.rules) {
			if (query.rules.length > 0) {
				newSearchFilter.current = processFilterArray(query.rules, query.combinator);
				previewContacts();
			} else {
				//If we are missing any rules, clear the data
				setTotal(0);
			}
		}

		return;
	}

	const processFilterArray = (items, combinator) => {
		const ruleArray = [];
		if (userContext.tenantId) {
			const ruleGroup = { [combinator]: ruleArray };
			//the rule array always has to have the tenant included for security
			_.forEach(items, (item) => { ruleArray.push(processFilterItem(item, items.combinator)) });
			const tenantRuleGroup = { and: [{ tenant: { eq: userContext.tenantId } }, ruleGroup] };
			return tenantRuleGroup;
		} else {
			return {}
		}
	}

	const processFilterItem = (item) => {
		const cpyItem = { ...item };
		if (item.field) {
			//item.value = item.value.toLowerCase();
			if (!item.field.startsWith('custom:')) {
				if (cpyItem.operator === 'wildcard') {
					cpyItem.value = cpyItem.value.toLowerCase();
					cpyItem.value = `*${cpyItem.value}*`;
				}
				if (cpyItem.operator === 'wildcard*') {
					cpyItem.value = cpyItem.value.toLowerCase();
					cpyItem.value = `${cpyItem.value}*`;
					cpyItem.operator = 'wildcard';
				}
				if (cpyItem.operator === '*wildcard') {
					cpyItem.value = cpyItem.value.toLowerCase();
					cpyItem.value = `*${cpyItem.value}`;
					cpyItem.operator = 'wildcard';
				}
				const ruleItem = { [cpyItem.field]: { [cpyItem.operator]: cpyItem.value } };
				return ruleItem;
			} else {
				let fieldName = cpyItem.field.split(':')[1];
				if (cpyItem.operator === 'wildcard') {
					cpyItem.value = `*${cpyItem.value}*`;
					fieldName += '.keyword';
				} else if (cpyItem.operator === 'wildcard*') {
					cpyItem.value = `${cpyItem.value}*`;
					cpyItem.operator = 'wildcard';
					fieldName += '.keyword';
				} else if (cpyItem.operator === '*wildcard') {
					cpyItem.value = `*${cpyItem.value}`;
					cpyItem.operator = 'wildcard';
					fieldName += '.keyword';
				} else {
					if (cpyItem.value === true || cpyItem.value === false || cpyItem.value === 'true' || cpyItem === 'false') {
						if (_.isString(cpyItem.value)) {
							cpyItem.value = cpyItem.value === 'true';
						} else {
							cpyItem.value = cpyItem.value === true;
						}
					} else if (!isNaN(cpyItem.value)) {
						cpyItem.value = +cpyItem.value;
					} else if (isCustomFieldDate(fieldName) && !!Date.parse(cpyItem.value)) {
						cpyItem.value = Date.parse(cpyItem.value);
					}
				}
				const ruleItem = { [`customFields.${fieldName}`]: { [cpyItem.operator]: cpyItem.value } };
				return ruleItem;
			}
		}

		return processFilterArray(cpyItem.rules, cpyItem.combinator);
	}

	const isCustomFieldDate = (fieldName) => {
		const customField = _.find(userContext.customFields, ['name', fieldName]);
		if (customField) {
			return customField.type === 'Date';
		} else {
			return false;
		}
	}

	const previewContacts = async () => {
		try {
			let options = {
				limit: 250
			};
			options.filter = JSON.stringify(newSearchFilter.current);

			const listData = await client.graphql({
				query: expandedContactSearch,
				variables: options,
			});

			// We need to parse the custom fields out and NOT stringify them when we send them back out
			// because when these contacts are put back into DynamoDB, it is done through a DocumentClient.
			// The DocumentClient takes AWSJSON as is (not stringified).
			for (const contact of listData.data.expandedContactSearch.items) {
				if (contact.customFields != null && typeof (contact.customFields) === 'string') {
					try {
						contact.customFields = JSON.parse(contact.customFields);
					} catch (_) { }
				}
			}

			const contacts = listData.data.expandedContactSearch.items;

			setRetrievedCount(contacts.length ? contacts.length : 0);
			setTotal(listData.data.expandedContactSearch.total ? listData.data.expandedContactSearch.total : 0);

			setContacts([...contacts]);
		} catch (error) {
			console.error(error);
		} finally {
			// In `finally` so a failed search stops showing progress too — the catch
			// above only logs, so nothing else would clear it.
			setPreviewLoading(false);
		}
	}

	function handleTypeChange(formikProps, e) {
		switch (valueInputTypes[e.target.value]) {
			default:
			case types.String:
			case types.ISODateTime:
			case types.DateTime:
			case types.Number:
			case types.Phone:
			case types.State:
			case types.Timezone:
				formikProps.setFieldValue('fieldValue', '');
				break;
			case types.Boolean:
				formikProps.setFieldValue('fieldValue', false);
				break;
		}

		formikProps.handleChange(e);
	}

	function customFieldUpdate(contact, field, value) {
		if (!('customFields' in contact)) {
			contact.customFields = {};
		}

		contact.customFields[field] = value;
	}

	function regularUpdate(contact, field, value) {
		contact[field] = value;
	}

	async function processContacts(values) {

		progressCountRef.current = 0;
		errorCountRef.current = 0;
		setProgressCount(0);
		setErrorCount(0);

		setConfirmOpen(false);
		// Retrieve each page of contacts and update them.
		if (mode === 'Update') {
			setProcessDialogTitle('Updating Contacts...');
		} else if (mode === 'Delete') {
			setProcessDialogTitle('Deleting Contacts...');
		} else if (mode === 'Scheduling') {
			setProcessDialogTitle('Scheduling Contacts...');
		} else {
			setProcessDialogTitle('Exporting Contacts...');
		}
		setProgressDialogOpen(true);

		// Fetch auth info once before processing to avoid repeated token refreshes
		let authToken;
		let updatedByName;
		try {
			const session = await fetchAuthSession();
			authToken = session.tokens.idToken;
			const attributes = await fetchUserAttributes();
			updatedByName = attributes.name;
		} catch (error) {
			console.error('Error fetching auth for bulk operation', error);
			enqueueSnackbar('Authentication error. Please refresh and try again.', { variant: 'error' });
			setProgressDialogOpen(false);
			return;
		}

		let options = {
			limit: 500
		};
		let nextToken = null;

		options.filter = JSON.stringify(newSearchFilter.current);
		do {
			if (nextToken) {
				options.nextToken = nextToken;
			}

			try {

				let listData = await client.graphql({
					query: expandedContactSearch,
					variables: options,
				});

				// We need to parse the custom fields out and NOT stringify them when we send them back out
				// because when these contacts are put back into DynamoDB, it is done through a DocumentClient.
				// The DocumentClient takes AWSJSON as is (not stringified).
				for (const contact of listData.data.expandedContactSearch.items) {
					if (contact.customFields != null && typeof (contact.customFields) === 'string') {
						try {
							contact.customFields = JSON.parse(contact.customFields);
						} catch (_) { }
					}
				}

				const retrievedContacts = listData.data.expandedContactSearch.items;

				nextToken = listData.data.expandedContactSearch.nextToken;

				const chunkedContacts = _.chunk(retrievedContacts, 100);

				if (mode !== 'Export') {

					const promises = [];
					for (const chunk of chunkedContacts) {
						promises.push(processBulkEdit(values, chunk, authToken, updatedByName));
					}

					await Promise.all(promises);

				} else {
					// Flatten the contacts into simple objects for CSV export
					const flatContacts = [];
					for (const contact of retrievedContacts) {
						const flatContact = { ...contact };
						if (flatContact.customFields) {
							for (const key in flatContact.customFields) {
								flatContact[`custom:${key}`] = flatContact.customFields[key];
							}
						}
						delete flatContact.customFields;
						flatContacts.push(flatContact);
					}
					csvChunks.current.push(...flatContacts);
				}

				progressCountRef.current += retrievedContacts.length;
				setProgressCount(progressCountRef.current);

			} catch (error) {
				const retrievedContacts = error.data.expandedContactSearch.items;
				const cleanedContacts = retrievedContacts.filter(contact => contact?.id);

				nextToken = error.data.expandedContactSearch.nextToken;

				const chunkedContacts = _.chunk(cleanedContacts, 100);

				if (mode !== 'Export') {
					const promises = [];
					for (const chunk of chunkedContacts) {
						promises.push(processBulkEdit(values, chunk, authToken, updatedByName));
					}

					await Promise.all(promises);
				} else {
					// Flatten the contacts into simple objects for CSV export
					const flatContacts = [];
					for (const contact of cleanedContacts) {
						const flatContact = { ...contact };
						if (flatContact.customFields) {
							for (const key in flatContact.customFields) {
								flatContact[`custom:${key}`] = flatContact.customFields[key];
							}
							delete flatContact.customFields;
							flatContacts.push(flatContact);
						}
						csvChunks.current.push(...flatContacts);
					}
				}

				errorCountRef.current += retrievedContacts.length - cleanedContacts.length;

				progressCountRef.current += retrievedContacts.length;
				setProgressCount(progressCountRef.current);
				setErrorCount(errorCountRef.current);
			}

		} while (nextToken);

		if (mode === 'Export') {
			await exportContacts();
		}

		setProgressDialogOpen(false);
		enqueueSnackbar(`Successful Contacts: ${progressCountRef.current} | Error Contacts: ${errorCountRef.current}`, {
			variant: 'success',
			autoHideDuration: 5000
		});

		setContacts([]);
		progressCountRef.current = 0;
		setTotal(0);
		setRetrievedCount(0);
	}

	async function processBulkEdit(values, contacts, authToken, updatedByName) {
		if (mode === 'Update' || mode === 'Scheduling') {
			const value = standardizeValue(values);
			try {
				const response = await post({
					apiName: 'cdyxoutreach',
					path: '/bulk-edit/edit',
					options: {
						headers: {
							Authorization: `Bearer ${authToken}`,
							'x-api-key': userContext.apiKey
						},
						body: {
							contacts,
							updateField: values.fieldName,
							updateValue: value,
							updatedBy: updatedByName
						}
					}
				}).response;

			} catch (err) {
				console.error('Unable to update contacts', err);
				enqueueSnackbar('Unable to update contacts', {
					variant: 'error',
					autoHideDuration: 5000
				});
			}
		} else {
			try {
				const response = await post({
					apiName: 'cdyxoutreach',
					path: '/bulk-edit/delete',
					options: {
						headers: {
							Authorization: `Bearer ${authToken}`,
							'x-api-key': userContext.apiKey
						},
						body: {
							contacts,
							deletedBy: updatedByName
						}
					}
				}).response;
			} catch (err) {
				console.error('Unable to delete contacts', err);
				enqueueSnackbar('Unable to delete contacts', {
					variant: 'error',
					autoHideDuration: 5000
				});
			}
		}

	}

	const handleCancelUpdate = () => {
		setConfirmOpen(false);
	}

	async function exportContacts() {
		const csvRows = [];
		const allChunks = csvChunks.current.flat(); // flatten all pages into one array

		if (!allChunks.length) {
			console.warn("No contacts to export.");
			return;
		}

		// build headers from all unique keys
		const headers = Array.from(
			allChunks.reduce((set, obj) => {
				Object.keys(obj).forEach(k => set.add(k));
				return set;
			}, new Set())
		);

		csvRows.push(headers.join(","));

		for (const contact of allChunks) {
			const row = headers
				.map(h => {
					let val = contact[h];
					if (val === undefined || val === null) return "";
					if (typeof val === "object") val = JSON.stringify(val);
					// Escape double quotes
					return `"${String(val).replace(/"/g, '""')}"`;
				})
				.join(",");
			csvRows.push(row);
		}

		const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "contacts.csv";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}


	return (
		<Box display='flex' flexDirection='column'>
			<Formik
				initialValues={{
					fieldName: '',
					fieldValue: '',
					query: {
						rules: [],
						combinator: 'and'
					}
				}}
				enableReinitialize={true}
				validationSchema={lazy(value => {
					let subSchema = mixed();

					try {
						subSchema = typeof (value.fieldName) === 'string' ? reach(contactValidation(userContext.customFields), value.fieldName.startsWith('custom:') ? `customFields.${value.fieldName.substring(7)}` : value.fieldName) : mixed();
					} catch (_) { }

					if (mode === 'Delete' || mode === 'Export') {
						return object({
							query: object({
								rules: array().min(1, 'You must have at least one rule in your query')
							})
						})
					}

					return object({
						fieldName: string().required('Please specify a field name'),
						fieldValue: subSchema,
						query: object({
							rules: array().min(1, 'You must have at least one rule in your query')
						})
					})
				})}
				onSubmit={values => {
					setConfirmOpen(true);
				}}
			>
				{formikProps => (
					<form onSubmit={formikProps.handleSubmit}>
						<Box display='flex' flexDirection='column' gap={2}>
							<PageAppBar
								title='Bulk Edit & Delete'
							// description="Bulk edit or delete contacts based on a query."
							/>

							<Card style={cardStyle} variant='outlined' >
								<CardHeader title="Query Builder" slotProps={{
									title: {
										variant: 'h6'
									}
								}}
									subheader={formikProps.errors?.query?.rules && formikProps.touched?.query?.rules ? formikProps.errors.query.rules : ''}
									// The header row is top-aligned, and so is the action, because the
									// action column changes height: the source-segment chip and its
									// caption appear once a segment is loaded. Centering either one makes
									// the title re-center against the taller action and jump down the
									// moment the attribution renders. Anchoring both to the top keeps the
									// title where it was. The action's negative default margins are also
									// reset — they are meant for an icon button and leave a form control
									// floating outside the header.
									sx={{
										alignItems: 'flex-start',
										// The `size="small"` picker's own label border puts its text a few
										// pixels below the top of its box, so a bare top alignment reads as
										// the title sitting high. A fixed nudge — not a centering rule —
										// lines the two up without reintroducing the jump.
										'& .MuiCardHeader-content': {
											paddingTop: '4px'
										},
										'& .MuiCardHeader-action': {
											alignSelf: 'flex-start',
											margin: 0
										}
									}}
									action={
										<Box sx={{
											// The Autocomplete would otherwise collapse to its content
											// width in the action slot, so the column is sized here and
											// the picker stretches to fill it.
											width: { xs: '240px', sm: '320px' },
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'flex-end',
											gap: 0.5
										}}>
											<Box sx={{ width: '100%' }}>
												<SegmentPicker
													tenantId={userContext.tenantId}
													value={selectedSegment}
													onChange={segment => handleSegmentSelect(segment, formikProps)}
												/>
											</Box>
											{sourceSegment &&
												<>
													<Chip size='small' label={sourceSegment.name} />
													<Typography variant='caption' color='textSecondary' sx={{ textAlign: 'right' }}>
														Editable copy. Changes here do not modify the segment.
													</Typography>
												</>}
										</Box>
									}
								/>
								<CardContent>
									{userContext.queryFields?.list && <QueryBuilder
										name='query'
										query={formikProps.values.query}
										fields={userContext.queryFields.list}
										onQueryChange={e => applyQueryChange(e, formikProps)}
										showCombinatorsBetweenRules
										getValueEditorType={field => userContext.queryFields.typeLookup(field)}
									/>}
								</CardContent>
							</Card>
							<Box
								sx={{
									display: 'grid',
									// Bulk Edit spans the full width on its own row: it holds a field
									// picker, a value editor and two buttons, which will not fit in a
									// shared column. Delete and Export are button-only and share the
									// row beneath it.
									gridTemplateColumns: {
										xs: '1fr',
										lg: 'minmax(0, 3fr) minmax(0, 2fr)'
									},
									gap: 2,
									alignItems: 'start'
								}}
							>

								<Card
									style={cardStyle}
									variant="outlined"
									sx={{ gridColumn: '1 / -1' }}>
									<CardHeader title='Bulk Edit' slotProps={{
										title: {
											variant: 'h6'
										}
									}} />
									<CardContent>
										<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 1.5 }} >
											<Typography variant="body2" color="textSecondary" >Set</Typography>
											<Box>
												<FormControl sx={{ minWidth: '220px' }}>
													<InputLabel error={formikProps.errors.fieldName && formikProps.touched.fieldName}>Field Name</InputLabel>
													<Select
														labelId='field-name'
														label='Field Name'
														error={formikProps.errors.fieldName && formikProps.touched.fieldName}
														name="fieldName"
														value={formikProps.values.fieldName}
														onChange={e => handleTypeChange(formikProps, e)}
														onBlur={formikProps.handleBlur}>
														{queryFields.map((field, index) =>
															<MenuItem key={index} value={field.name}>{field.label}</MenuItem>)}
													</Select>
													{formikProps.errors.fieldName && formikProps.touched.fieldName &&
														<FormHelperText error>{formikProps.errors.fieldName}</FormHelperText>}
												</FormControl>
											</Box>
											<Typography variant='body2' color="textSecondary" >to</Typography>
											<Box sx={{ minWidth: '220px' }}>
												{function () {
													switch (valueInputTypes[formikProps.values.fieldName]) {
														case types.String:
														case types.ISODateTime:
														case types.DateTime:
														case types.Number:
														case types.Phone:
															return (
																<TextField
																	label='Field Value'
																	name='fieldValue'
																	type={valueInputTypes[formikProps.values.fieldName] === types.String || valueInputTypes[formikProps.values.fieldName] === types.Phone
																		? 'text'
																		: valueInputTypes[formikProps.values.fieldName] === types.ISODateTime || valueInputTypes[formikProps.values.fieldName] === types.DateTime
																			? 'datetime-local'
																			: 'number'}
																	value={formikProps.values.fieldValue}
																	onChange={formikProps.handleChange}
																	onBlur={formikProps.handleBlur}
																	error={formikProps.errors?.fieldValue && formikProps.touched?.fieldValue}
																	helperText={formikProps.touched?.fieldValue && formikProps.errors?.fieldValue}
																/>
															);
														case types.State:
															return (
																<FormControl>
																	<InputLabel error={formikProps.errors?.fieldValue && formikProps.touched?.fieldValue}>Field Value</InputLabel>
																	<Select sx={{ minWidth: '200px' }} name='fieldValue' label="Field Value" onChange={formikProps.handleChange} onBlur={formikProps.handleBlur} value={formikProps.values.fieldValue}>
																		{usStates.map(state =>
																			<MenuItem key={state[1]} value={state[1]}>{state[0]}</MenuItem>)}
																	</Select>
																	{formikProps.errors?.fieldValue && formikProps.touched?.fieldValue &&
																		<FormHelperText error>{formikProps.errors.fieldValue}</FormHelperText>}
																</FormControl>
															);
														case types.Boolean:
															return (
																<FormControlLabel label='Field Value' control={
																	<Switch name='fieldValue' checked={formikProps.values.fieldValue} onChange={formikProps.handleChange} />
																} />
															);
														default:
															return null;
													}
												}()}
											</Box>
											{formikProps.values.fieldName &&
												<Box sx={{
													display: 'flex',
													flexWrap: 'wrap',
													gap: 1,
													// Right aligned on wide screens, wraps onto its own
													// line once the controls fill the row.
													marginLeft: { lg: 'auto' }
												}}>
													<Button sx={actionOneButtonStyle} type='submit' onClick={() => setMode('Update')}>Update {total} Contacts</Button>
													<Button
														sx={actionTwoButtonStyle}
														startIcon={<EventRepeatOutlined />}
														onClick={() => {
															setRecurringAction('Update');
															setRecurringDialogOpen(true);
														}}>Schedule Recurring</Button>
												</Box>}
										</Box>
									</CardContent>
								</Card>
								<Card
									style={cardStyle}
									variant="outlined"
								>
									<CardHeader title='Bulk Delete' slotProps={{
										title: {
											variant: 'h6'
										}
									}} />
									<CardContent>
										<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }} >
											<Button sx={destructiveButtonStyle} type='submit' onClick={() => setMode('Delete')}>{`Delete ${total} Contacts`}</Button>
											<Button sx={actionTwoButtonStyle} startIcon={<ScheduleOutlined />} onClick={() => setScheduleDeleteOpen(true)}>Schedule Delete</Button>
											<Button
												sx={actionTwoButtonStyle}
												startIcon={<EventRepeatOutlined />}
												onClick={() => {
													setRecurringAction('Delete');
													setRecurringDialogOpen(true);
												}}>Schedule Recurring</Button>
										</Box>
									</CardContent>
								</Card>
								<Card
									style={cardStyle}
									variant="outlined"
								>
									<CardHeader title='Bulk Export' slotProps={{
										title: {
											variant: 'h6'
										}
									}} />
									<CardContent>
										<Button sx={actionOneButtonStyle} type='submit' onClick={() => setMode('Export')}>{`Export ${total} Contacts`}</Button>
									</CardContent>
								</Card>
							</Box>

							<Card variant='outlined' style={cardStyle}>
								<CardContent>
									<MaterialTable
										components={{
											// Toolbar: toolBar,
											Container: (props) => <Box {...props} elevation={0} />,
										}}
										title={previewLoading
											? 'Searching contacts…'
											: `Preview ${retrievedCount} of ${total} matched contacts.`}
										isLoading={previewLoading}
										columns={columns}
										data={contacts}
										options={{
											actionsColumnIndex: -1,
											pageSize: 5,
											searchFieldStyle: {
												marginBottom: '16px',
												marginleft: '-28px',
											},
											headerStyle: {
												borderTop: '1px solid #e0e0e0',
												padding: '16px',
											},
											searchFieldVariant: 'outlined'
										}}
										onRowClick={(event, rowData) => {
											navigate(`/contacts/${rowData.id}`);
										}}
									/>
								</CardContent>
							</Card>

						</Box>

						<ConfirmDialog
							open={confirmOpen}
							title={`${mode === 'Update' ? 'Update' : mode === 'Delete' ? 'Delete' : 'Export'} ${total} Contacts`}
							description={`Are you sure you want to ${mode === 'Update' ? 'update' : mode === 'Delete' ? 'delete' : 'export'} these contacts?`}
							actionOneText={mode}
							actionOneHandler={() => processContacts(formikProps.values)}
							actionTwoText="Cancel"
							actionTwoHandler={handleCancelUpdate}
						/>
						{/* Separate from the update/delete/export confirmation above: this one
						    guards the rules already in the query builder against a segment load. */}
						<ConfirmDialog
							open={replaceConfirmOpen}
							title='Replace Query Rules'
							description={`Replace the ${(formikProps.values.query?.rules ?? []).length} rule${(formikProps.values.query?.rules ?? []).length === 1 ? '' : 's'} in the query builder with the query from ${pendingSegment?.segment?.name ?? 'the selected segment'}? Your current rules will be discarded.`}
							actionOneText='Replace'
							actionOneHandler={() => handleConfirmReplace(formikProps)}
							actionTwoText='Cancel'
							actionTwoHandler={handleCancelReplace}
						/>
						<Dialog open={progressDialogOpen}>
							<DialogTitle>{processDialogTitle}</DialogTitle>
							<DialogContent>
								<Typography variant='subtitle2' >Processed {progressCount} of {total} contacts...</Typography>
								<LinearProgress variant='determinate' value={progressCount / total * 100} />
							</DialogContent>
						</Dialog>

						<Dialog open={scheduleDeleteOpen}>
							<DialogTitle>Schedule Delete</DialogTitle>
							<DialogContent>
								<TextField
									label='Scheduled Delete Time'
									type='datetime-local'
									sx={{ minWidth: '250px', marginTop: '16px' }}
									value={deleteTime}
									onChange={e => setDeleteTime(e.target.value)}
									InputLabelProps={{
										shrink: true,
									}}
								/>
							</DialogContent>
							<DialogActions>
								<Box display="flex" gap={2}>
									<Button sx={actionTwoButtonStyle} onClick={() => setScheduleDeleteOpen(false)}>Cancel</Button>
									<Button sx={destructiveButtonStyle} onClick={() => {
										// Set to an update and update the ttl on the contact to the time in epoch
										setMode('Scheduling');
										processContacts({ ...formikProps.values, fieldName: 'ttl', fieldValue: deleteTime });
										setScheduleDeleteOpen(false);
									}}>Schedule Delete for {total} Contacts</Button>
								</Box>

							</DialogActions>
						</Dialog>

						<BulkScheduleDialog
							open={recurringDialogOpen}
							onClose={() => setRecurringDialogOpen(false)}
							action={recurringAction}
							qbQuery={formikProps.values.query}
							getSearchFilter={() => processFilterArray(formikProps.values.query.rules, formikProps.values.query.combinator)}
							updateField={recurringAction === 'Update' ? formikProps.values.fieldName : undefined}
							updateValue={recurringAction === 'Update' ? standardizeValue(formikProps.values) : undefined}
							updateValueRaw={recurringAction === 'Update' ? formikProps.values.fieldValue : undefined}
							matchedCount={total}
							sourceSegment={sourceSegment}
						/>
					</form>
				)}
			</Formik>
		</Box>
	)
}
